import { ParamValues } from '../../../components/Crud/useFilter.types';
import { FetcherOptions } from '../../../components/Hooks/useList.types';
import { fetcher } from '../../../components/Hooks/useListActions';
import createRepository, { ErrorResponse, handlerApiError } from '../../../lib/basicRepository';
import { restApi } from '../../../services/api';
import { PaginatedResponse } from '../../../types/request.types';
import { cancelTokenFromSignal } from '../../../utils/axios.utils';
import { Catalog, CatalogSectionCount } from '../catalog.types';
import { GetSimplifiedCatalogSectionItem } from '../dtos/catalog-repository.dtos';
import { ESProductCatalogSearch } from '../products/product-catalog-es.types';
import {
  BatchUpdateCatalogSectionsParams,
  GetCatalogSectionsOnlineOutput,
  GetCatalogSectionsOutput,
  GetSellInPriceRangeOutput,
  GetSellInPriceRangeParams,
  OfflineSectionProductSet,
  UpdateCatalogNavigationPreferencesParams,
  UpdateCatalogParams,
  UpdateCatalogViewPreferencesParams,
} from './types/catalogs.repository.types';

export const fetchCatalogs = (params?: { active: boolean }, signal?: AbortSignal) =>
  restApi.get<PaginatedResponse<Catalog>>('catalogs', params, {
    cancelToken: cancelTokenFromSignal(signal)?.token,
  });

export const onlineCatalogsRepository = (id: string) => {
  const defaultRepository = createRepository({
    path: 'catalogs',
    id,
    restApi,
  });

  const repository = {
    ...defaultRepository,

    addProducts: (data: any) => restApi.post(`catalogs/${id}/products`, data),

    moveProductsToSection: (
      data: any,
      params?: { searchParams: ParamValues; isAdminSeller: boolean; limit: number; skip: number },
    ) =>
      restApi.patch(`catalogs/${id}/products`, data, {
        params: {
          ...params?.searchParams,
          isAdminSeller: params?.isAdminSeller,
          limit: params?.limit,
          skip: params?.skip,
        },
      }),

    share: (data: any) => restApi.post(`catalogs/share`, data),

    getCatalogSections: async (params: ParamValues): Promise<GetCatalogSectionsOutput> => {
      const sections = await restApi.get<CatalogSectionCount[]>(
        `catalogs/${id}/products-count`,
        params,
      );
      const output: GetCatalogSectionsOnlineOutput = { sectionsResponse: sections };
      return output;
    },

    syncDynamicCatalog: () => restApi.post(`catalogs/catalog-sync/${id}`),

    updateCatalog: (data: UpdateCatalogParams) => restApi.patch<Catalog>(`catalogs/${id}`, data),

    updateNavigationPreferences: (data: UpdateCatalogNavigationPreferencesParams) =>
      restApi.patch<Catalog>(`catalogs/${id}/navigation-preferences`, data),

    updateViewPreferences: (data: UpdateCatalogViewPreferencesParams) =>
      restApi.patch<Catalog>(`catalogs/${id}/view-preferences`, data),

    batchUpdateCatalogSections: (data: BatchUpdateCatalogSectionsParams) =>
      restApi.patch<void>(`catalogs/${id}/sections/batch`, data),
    getSimplifiedProducts: (catalogSectionId: string) =>
      restApi.get<GetSimplifiedCatalogSectionItem[]>(
        `/catalogs/${id}/sections/${catalogSectionId}/simplified-products?limit=99999`,
      ),

    addKitsToCatalog: (kitIds: string[]) => restApi.post(`catalogs/${id}/kits`, { kitIds }),

    getESProductCatalogSearch: (
      options: FetcherOptions,
      _offlineSectionProductSet: OfflineSectionProductSet,
    ) =>
      fetcher<ESProductCatalogSearch, ESProductCatalogSearch>(`/catalogs/${id}/products`)(options),

    getSellInPriceRange: async (params: GetSellInPriceRangeParams) =>
      handlerApiError(
        await restApi.get<GetSellInPriceRangeOutput, ErrorResponse>(
          `/catalogs/${id}/sell-in-price-range`,
          params,
        ),
      ),
  };

  return repository;
};
